import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, CreditCard, CheckCircle, XCircle, ArrowLeft, ChevronLeft, ChevronRight, Upload, User, Wallet } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import SEO from '../components/SEO';
import { SITE_CONFIG } from '../config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASTROLOGY_PACKAGES = SITE_CONFIG.prices.astrology;
const VASTU_PACKAGES = SITE_CONFIG.prices.vastu;

const BookingPage = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHindi = i18n.language === 'hi';

  // Restore saved booking state after login redirect
  const savedState = JSON.parse(sessionStorage.getItem('booking_state') || 'null');

  const [step, setStep] = useState(savedState?.step || 1);
  const [serviceType, setServiceType] = useState(savedState?.serviceType || location.state?.serviceType || 'astrology');
  const [packageType, setPackageType] = useState(savedState?.packageType || location.state?.packageType || '');
  const [selectedDate, setSelectedDate] = useState(savedState?.selectedDate || '');
  const [selectedTime, setSelectedTime] = useState(savedState?.selectedTime || '');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [fullyBooked, setFullyBooked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [bookingId, setBookingId] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [astroDetails, setAstroDetails] = useState(
    savedState?.astroDetails || { name: user?.name || '', phone: user?.phone || '', dob: '', birth_time: '', birth_place: '', info_box: '' }
  );
  const [vastuDetails, setVastuDetails] = useState(
    savedState?.vastuDetails || { name: user?.name || '', phone: user?.phone || '', info_box: '' }
  );
  const [floorPlanFile, setFloorPlanFile] = useState(null);

  // Clear saved state after restoring
  useEffect(() => {
    if (savedState && isAuthenticated) {
      sessionStorage.removeItem('booking_state');
    }
  }, [isAuthenticated, savedState]);

  const getToken = useCallback(() => localStorage.getItem('token'), []);

  const fetchSlots = useCallback(async (date) => {
    try {
      const res = await axios.get(`${API}/slots/available?date=${date}`);
      setAvailableSlots(res.data.slots);
      setFullyBooked(res.data.fully_booked);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { if (selectedDate) fetchSlots(selectedDate); }, [selectedDate, fetchSlots]);
  useEffect(() => {
    if (user && !savedState) {
      setAstroDetails(prev => ({ ...prev, name: user.name || prev.name, phone: user.phone || prev.phone }));
      setVastuDetails(prev => ({ ...prev, name: user.name || prev.name, phone: user.phone || prev.phone }));
    }
  }, [user, savedState]);

  const selectedPackage = serviceType === 'astrology' ? ASTROLOGY_PACKAGES[packageType] : VASTU_PACKAGES[packageType];
  const isFirstTimeFree = user && !user.first_time_free_used;
  const discount = isFirstTimeFree ? SITE_CONFIG.firstTimeFreeDiscount : 0;
  const finalPrice = Math.max(0, (selectedPackage?.price || 0) - discount);

  // Save state before redirecting to login
  const saveAndLogin = () => {
    sessionStorage.setItem('booking_state', JSON.stringify({
      step, serviceType, packageType, selectedDate, selectedTime, astroDetails, vastuDetails
    }));
    navigate('/login', { state: { from: '/booking' } });
  };

  const handlePayment = async () => {
    if (!isAuthenticated) { saveAndLogin(); return; }
    setLoading(true);
    try {
      const token = getToken();
      const endpoint = serviceType === 'astrology' ? '/bookings/astrology' : '/bookings/vastu';
      const infoText = serviceType === 'astrology'
        ? `Name: ${astroDetails.name}, Phone: ${astroDetails.phone}, DOB: ${astroDetails.dob}, Birth Time: ${astroDetails.birth_time}, Birth Place: ${astroDetails.birth_place}. Notes: ${astroDetails.info_box}`
        : `Name: ${vastuDetails.name}, Phone: ${vastuDetails.phone}. Notes: ${vastuDetails.info_box}`;

      const payload = serviceType === 'astrology'
        ? { package_type: packageType, booking_date: selectedDate, booking_time: selectedTime, info_box: infoText }
        : { service_type: packageType, booking_date: selectedDate, booking_time: selectedTime, info_box: infoText };

      const res = await axios.post(`${API}${endpoint}`, payload, { headers: { Authorization: `Bearer ${token}` } });

      if (serviceType === 'vastu' && floorPlanFile) {
        const formData = new FormData();
        formData.append('file', floorPlanFile);
        try {
          await axios.post(`${API}/upload/floor-plan?description=Floor plan for booking ${res.data.booking.booking_id}`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
        } catch (uploadErr) { console.error('Upload error:', uploadErr); }
      }

      const { razorpay_order_id, razorpay_key_id, booking } = res.data;

      const loadRazorpay = () => {
        const options = {
          key: razorpay_key_id, amount: booking.amount * 100, currency: 'INR',
          name: 'Astrology Times', description: selectedPackage?.name || 'Consultation',
          order_id: razorpay_order_id,
          handler: async function (response) {
            try {
              const verifyEndpoint = serviceType === 'astrology' ? '/bookings/astrology/verify' : '/bookings/vastu/verify';
              await axios.post(
                `${API}${verifyEndpoint}?booking_id=${booking.booking_id}&razorpay_payment_id=${response.razorpay_payment_id}&razorpay_signature=${response.razorpay_signature}`,
                {}, { headers: { Authorization: `Bearer ${token}` } }
              );
              setBookingId(booking.booking_id);
              setPaymentStatus('success');
              if (checkAuth) checkAuth();

              // Auto-open WhatsApp notification to admin
              const whatsappMsg = encodeURIComponent(
                `Message from Astrology Times App\nNew Booking Received!\nClient: ${serviceType === 'astrology' ? astroDetails.name : vastuDetails.name}\nService: ${selectedPackage?.name}\nDate: ${selectedDate}\nTime: ${selectedTime}\nPhone: ${serviceType === 'astrology' ? astroDetails.phone : vastuDetails.phone}\nAmount Paid: Rs.${booking.amount}\nBooking ID: ${booking.booking_id}`
              );
              window.open(`https://wa.me/${SITE_CONFIG.whatsappNumber1}?text=${whatsappMsg}`, '_blank');
            } catch (err) { setPaymentStatus('failed'); }
          },
          prefill: {
            name: serviceType === 'astrology' ? astroDetails.name : vastuDetails.name,
            email: user?.email || '',
            contact: serviceType === 'astrology' ? astroDetails.phone : vastuDetails.phone
          },
          theme: { color: '#C9A84C' },
          // Enable ALL payment methods — UPI (PhonePe/GPay/Paytm), Card, NetBanking, Wallet
          // Let Razorpay auto-render whichever methods are enabled on the merchant account.
          method: { upi: true, card: true, netbanking: true, wallet: true, emi: true, paylater: true },
          modal: { ondismiss: () => setPaymentStatus('failed') }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
      };

      if (window.Razorpay) { loadRazorpay(); }
      else {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = loadRazorpay;
        document.body.appendChild(script);
      }
    } catch (err) { console.error(err); setPaymentStatus('failed'); }
    finally { setLoading(false); }
  };

  // Wallet payment
  const handleWalletPay = async () => {
    if (!isAuthenticated) return;
    if ((user?.wallet_balance || 0) < finalPrice) {
      alert(isHindi ? 'अपर्याप्त वॉलेट बैलेंस' : 'Insufficient wallet balance');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const endpoint = serviceType === 'astrology' ? '/bookings/astrology' : '/bookings/vastu';
      const infoText = serviceType === 'astrology'
        ? `Name: ${astroDetails.name}, Phone: ${astroDetails.phone}, DOB: ${astroDetails.dob}, Birth Time: ${astroDetails.birth_time}, Birth Place: ${astroDetails.birth_place}`
        : `Name: ${vastuDetails.name}, Phone: ${vastuDetails.phone}`;
      const payload = serviceType === 'astrology'
        ? { package_type: packageType, booking_date: selectedDate, booking_time: selectedTime, info_box: infoText }
        : { service_type: packageType, booking_date: selectedDate, booking_time: selectedTime, info_box: infoText };
      const res = await axios.post(`${API}${endpoint}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      await axios.post(`${API}/bookings/pay-with-wallet`, { booking_id: res.data.booking.booking_id, booking_type: serviceType }, { headers: { Authorization: `Bearer ${token}` } });
      setBookingId(res.data.booking.booking_id);
      setPaymentStatus('success');
      if (checkAuth) checkAuth();
      const whatsappMsg = encodeURIComponent(`Message from Astrology Times App\nNew Booking!\nClient: ${serviceType === 'astrology' ? astroDetails.name : vastuDetails.name}\nService: ${selectedPackage?.name}\nDate: ${selectedDate}\nTime: ${selectedTime}\nBooking ID: ${res.data.booking.booking_id}`);
      window.open(`https://wa.me/${SITE_CONFIG.whatsappNumber1}?text=${whatsappMsg}`, '_blank');
    } catch (err) { alert(err.response?.data?.detail || 'Payment failed'); }
    setLoading(false);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return { firstDay: new Date(year, month, 1).getDay(), daysInMonth: new Date(year, month + 1, 0).getDate() };
  };
  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth);
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Success page
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 flex items-center justify-center">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
          <CheckCircle className="w-20 h-20 text-[#C9A84C] mx-auto mb-6" />
          <h2 className="text-3xl font-serif text-[#F5F2EB] mb-4" data-testid="payment-success-heading">{isHindi ? 'भुगतान सफल!' : 'Payment Successful!'}</h2>
          <p className="text-[#A3A3A3] mb-2">Booking ID: <span className="text-[#C9A84C] font-bold">{bookingId}</span></p>
          <p className="text-[#A3A3A3] mb-6">{isHindi ? 'WhatsApp पर सूचना भेजी गई है।' : 'WhatsApp notification has been sent.'}</p>
          <div className="space-y-3">
            <button onClick={() => navigate('/dashboard')} className="block w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870]">{isHindi ? 'डैशबोर्ड देखें' : 'Go to Dashboard'}</button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 flex items-center justify-center">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
          <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
          <h2 className="text-3xl font-serif text-[#F5F2EB] mb-4">{isHindi ? 'भुगतान विफल' : 'Payment Failed'}</h2>
          <button onClick={() => setPaymentStatus(null)} className="w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870]">{isHindi ? 'पुनः प्रयास' : 'Try Again'}</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-16">
      <SEO page="booking" />
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-serif text-[#F5F2EB] text-center mb-8" data-testid="booking-page-heading">
          {isHindi ? 'परामर्श बुक करें' : 'Book Consultation'}
        </h1>

        <div className="flex justify-center gap-4 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`w-10 h-10 flex items-center justify-center border ${step >= s ? 'bg-[#C9A84C] text-[#0A0A0A] border-[#C9A84C]' : 'border-[#C9A84C]/30 text-[#A3A3A3]'}`}>{s}</div>
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-serif text-[#F5F2EB] mb-6">{isHindi ? 'सेवा चुनें' : 'Select Service'}</h2>
            <div className="flex gap-4 mb-6">
              <button onClick={() => { setServiceType('astrology'); setPackageType(''); }} className={`flex-1 py-3 border ${serviceType === 'astrology' ? 'border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]' : 'border-[#C9A84C]/20 text-[#A3A3A3]'}`} data-testid="service-astrology-btn">{isHindi ? 'ज्योतिष' : 'Astrology'}</button>
              <button onClick={() => { setServiceType('vastu'); setPackageType(''); }} className={`flex-1 py-3 border ${serviceType === 'vastu' ? 'border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]' : 'border-[#C9A84C]/20 text-[#A3A3A3]'}`} data-testid="service-vastu-btn">{isHindi ? 'वास्तु' : 'Vastu'}</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(serviceType === 'astrology' ? ASTROLOGY_PACKAGES : VASTU_PACKAGES).map(([key, pkg]) => (
                <button key={key} onClick={() => setPackageType(key)} className={`text-left p-4 border ${packageType === key ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-[#C9A84C]/15 bg-[#1A1A1A]'} hover:border-[#C9A84C]/50`} data-testid={`package-${key}-btn`}>
                  <h3 className="text-[#F5F2EB] font-medium">{isHindi ? pkg.nameHi : pkg.name}</h3>
                  {pkg.duration && <p className="text-[#A3A3A3] text-sm">{pkg.duration} min</p>}
                  <p className="text-[#C9A84C] text-xl font-bold mt-1">₹{pkg.price.toLocaleString()}</p>
                </button>
              ))}
            </div>
            <button onClick={() => packageType && setStep(2)} disabled={!packageType} className="mt-6 w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] disabled:opacity-40 uppercase tracking-wider text-sm" data-testid="step1-next-btn">{isHindi ? 'अगला' : 'Next'}</button>
          </motion.div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={() => setStep(1)} className="flex items-center gap-2 text-[#C9A84C] mb-6"><ArrowLeft className="w-4 h-4" /> {isHindi ? 'पीछे' : 'Back'}</button>

            {!isAuthenticated && (
              <div className="bg-[#C9A84C]/10 border border-[#C9A84C]/30 p-6 mb-6 text-center" data-testid="login-prompt">
                <p className="text-[#F5F2EB] mb-3">{isHindi ? 'बुकिंग जारी रखने के लिए कृपया पहले लॉगिन करें। लॉगिन के बाद आपकी भरी हुई जानकारी सुरक्षित रहेगी।' : 'Please login first. Your filled details will be saved and restored after login.'}</p>
                <button onClick={saveAndLogin} className="bg-[#C9A84C] text-[#0A0A0A] px-8 py-3 font-medium hover:bg-[#E5C870] uppercase tracking-wider text-sm" data-testid="booking-login-btn">{isHindi ? 'लॉगिन करें' : 'Login Now'}</button>
              </div>
            )}

            <h2 className="text-2xl font-serif text-[#F5F2EB] mb-6"><User className="inline w-6 h-6 mr-2 text-[#C9A84C]" />{isHindi ? 'व्यक्तिगत विवरण' : 'Personal Details'}</h2>

            {serviceType === 'astrology' ? (
              <div className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-6 space-y-4" data-testid="astro-details-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'नाम' : 'Full Name'} *</label><input value={astroDetails.name} onChange={(e) => setAstroDetails({ ...astroDetails, name: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" data-testid="astro-name" /></div>
                  <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'फोन' : 'Phone'} *</label><input value={astroDetails.phone} onChange={(e) => setAstroDetails({ ...astroDetails, phone: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" data-testid="astro-phone" /></div>
                  <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'जन्म तिथि' : 'Date of Birth'} *</label><input type="date" value={astroDetails.dob} onChange={(e) => setAstroDetails({ ...astroDetails, dob: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" data-testid="astro-dob" /></div>
                  <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'जन्म समय' : 'Birth Time'} *</label><input type="time" value={astroDetails.birth_time} onChange={(e) => setAstroDetails({ ...astroDetails, birth_time: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" data-testid="astro-birth-time" /></div>
                  <div className="md:col-span-2"><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'जन्म स्थान' : 'Birth Place'} *</label><input value={astroDetails.birth_place} onChange={(e) => setAstroDetails({ ...astroDetails, birth_place: e.target.value })} placeholder={isHindi ? 'शहर, राज्य' : 'City, State'} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" data-testid="astro-birth-place" /></div>
                </div>
                <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'आप क्या जानना चाहते हैं? (वैकल्पिक)' : 'What do you want to know? (Optional)'}</label><textarea value={astroDetails.info_box} onChange={(e) => setAstroDetails({ ...astroDetails, info_box: e.target.value })} rows={3} className="w-full bg-transparent border border-[#C9A84C]/30 p-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
              </div>
            ) : (
              <div className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-6 space-y-4" data-testid="vastu-details-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'नाम' : 'Name'} *</label><input value={vastuDetails.name} onChange={(e) => setVastuDetails({ ...vastuDetails, name: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
                  <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'फोन' : 'Phone'} *</label><input value={vastuDetails.phone} onChange={(e) => setVastuDetails({ ...vastuDetails, phone: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
                </div>
                <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'फ्लोर प्लान अपलोड' : 'Upload Floor Plan (PDF/Image)'}</label>
                  <div className="mt-2 border border-dashed border-[#C9A84C]/30 p-6 text-center"><Upload className="w-10 h-10 text-[#C9A84C] mx-auto mb-2" /><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFloorPlanFile(e.target.files[0])} className="text-[#A3A3A3] text-sm" />{floorPlanFile && <p className="text-[#C9A84C] text-sm mt-2">{floorPlanFile.name}</p>}</div>
                </div>
                <div><label className="text-xs text-[#A3A3A3] uppercase block mb-1">{isHindi ? 'अतिरिक्त जानकारी' : 'Additional Info (Optional)'}</label><textarea value={vastuDetails.info_box} onChange={(e) => setVastuDetails({ ...vastuDetails, info_box: e.target.value })} rows={3} className="w-full bg-transparent border border-[#C9A84C]/30 p-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
              </div>
            )}

            <button onClick={() => {
              if (!isAuthenticated) { saveAndLogin(); return; }
              if (serviceType === 'astrology' && (!astroDetails.name || !astroDetails.phone || !astroDetails.dob || !astroDetails.birth_time || !astroDetails.birth_place)) { alert(isHindi ? 'सभी फ़ील्ड भरें' : 'Fill all required fields'); return; }
              if (serviceType === 'vastu' && (!vastuDetails.name || !vastuDetails.phone)) { alert(isHindi ? 'नाम और फोन भरें' : 'Fill name and phone'); return; }
              setStep(3);
            }} className="mt-6 w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] uppercase tracking-wider text-sm" data-testid="step2-next-btn">{isHindi ? 'अगला' : 'Next'}</button>
          </motion.div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={() => setStep(2)} className="flex items-center gap-2 text-[#C9A84C] mb-6"><ArrowLeft className="w-4 h-4" /> {isHindi ? 'पीछे' : 'Back'}</button>
            <h2 className="text-2xl font-serif text-[#F5F2EB] mb-6"><Calendar className="inline w-6 h-6 mr-2 text-[#C9A84C]" />{isHindi ? 'तारीख और समय' : 'Appointment Date & Time'}</h2>

            <div className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="text-[#C9A84C]"><ChevronLeft className="w-6 h-6" /></button>
                <span className="text-[#F5F2EB] font-medium">{currentMonth.toLocaleDateString(isHindi ? 'hi-IN' : 'en-US', { month: 'long', year: 'numeric' })}</span>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="text-[#C9A84C]"><ChevronRight className="w-6 h-6" /></button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {(isHindi ? ['रवि', 'सोम', 'मंगल', 'बुध', 'गुरु', 'शुक्र', 'शनि'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']).map(d => <span key={d} className="text-[#A3A3A3] text-xs py-1">{d}</span>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }, (_, i) => <div key={`e-${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                  const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isPast = dateObj < today;
                  const isSelected = selectedDate === dateStr;
                  return <button key={day} onClick={() => !isPast && setSelectedDate(dateStr)} disabled={isPast} className={`py-2 text-sm ${isPast ? 'text-[#555] cursor-not-allowed' : isSelected ? 'bg-[#C9A84C] text-[#0A0A0A] font-bold' : 'text-[#F5F2EB] hover:bg-[#C9A84C]/20'}`}>{day}</button>;
                })}
              </div>
            </div>

            {selectedDate && (
              <div className="mb-6">
                <h3 className="text-lg text-[#F5F2EB] mb-3"><Clock className="inline w-5 h-5 mr-2 text-[#C9A84C]" />{isHindi ? 'उपलब्ध समय' : 'Available Slots'}</h3>
                {fullyBooked ? <p className="text-red-400 bg-red-400/10 border border-red-400/30 px-4 py-3">{isHindi ? 'यह दिन पूर्ण बुक है' : 'Fully booked'}</p> : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {availableSlots.map(slot => <button key={slot} onClick={() => setSelectedTime(slot)} className={`py-3 border text-sm ${selectedTime === slot ? 'border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]' : 'border-[#C9A84C]/15 text-[#F5F2EB] hover:border-[#C9A84C]/50'}`}>{slot}</button>)}
                  </div>
                )}
              </div>
            )}

            <button onClick={() => selectedDate && selectedTime && setStep(4)} disabled={!selectedDate || !selectedTime} className="w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] disabled:opacity-40 uppercase tracking-wider text-sm">{isHindi ? 'अगला' : 'Next'}</button>
          </motion.div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button onClick={() => setStep(3)} className="flex items-center gap-2 text-[#C9A84C] mb-6"><ArrowLeft className="w-4 h-4" /> {isHindi ? 'पीछे' : 'Back'}</button>
            <h2 className="text-2xl font-serif text-[#F5F2EB] mb-6"><CreditCard className="inline w-6 h-6 mr-2 text-[#C9A84C]" />{isHindi ? 'भुगतान सारांश' : 'Payment Summary'}</h2>

            <div className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-6 mb-6" data-testid="payment-summary">
              <div className="space-y-3">
                <div className="flex justify-between text-[#F5F2EB]"><span>{isHindi ? 'सेवा' : 'Service'}</span><span className="font-medium">{isHindi ? selectedPackage?.nameHi : selectedPackage?.name}</span></div>
                <div className="flex justify-between text-[#F5F2EB]"><span>{isHindi ? 'नाम' : 'Name'}</span><span>{serviceType === 'astrology' ? astroDetails.name : vastuDetails.name}</span></div>
                <div className="flex justify-between text-[#F5F2EB]"><span>{isHindi ? 'तारीख' : 'Date'}</span><span>{selectedDate}</span></div>
                <div className="flex justify-between text-[#F5F2EB]"><span>{isHindi ? 'समय' : 'Time'}</span><span>{selectedTime}</span></div>

                {isFirstTimeFree && (
                  <>
                    <div className="flex justify-between text-[#A3A3A3]"><span>{isHindi ? 'मूल राशि' : 'Original Price'}</span><span className="line-through">₹{selectedPackage?.price.toLocaleString()}</span></div>
                    <div className="flex justify-between text-green-400"><span>{isHindi ? 'पहले 10 मिनट मुफ्त (एक बार)' : 'First 10 Min FREE (one-time only)'}</span><span>- ₹{SITE_CONFIG.firstTimeFreeDiscount}</span></div>
                  </>
                )}

                <div className="border-t border-[#C9A84C]/15 pt-3 flex justify-between">
                  <span className="text-[#F5F2EB] font-bold text-lg">{isHindi ? 'कुल राशि' : 'Total'}</span>
                  <span className="text-[#C9A84C] font-bold text-2xl">₹{finalPrice.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Payment Options */}
            <div className="space-y-3">
              <button onClick={handlePayment} disabled={loading} className="w-full bg-[#C9A84C] text-[#0A0A0A] py-4 font-bold hover:bg-[#E5C870] disabled:opacity-40 uppercase tracking-wider text-sm" data-testid="razorpay-checkout-btn">
                {loading ? '...' : `${isHindi ? 'UPI / Card / NetBanking से भुगतान करें' : 'Pay via UPI / Card / NetBanking'} - ₹${finalPrice.toLocaleString()}`}
              </button>

              {isAuthenticated && (user?.wallet_balance || 0) > 0 && (
                <button onClick={handleWalletPay} disabled={loading || (user?.wallet_balance || 0) < finalPrice} className="w-full bg-transparent border border-[#C9A84C]/50 text-[#C9A84C] py-3 font-medium hover:bg-[#C9A84C]/10 disabled:opacity-40 uppercase tracking-wider text-sm flex items-center justify-center gap-2" data-testid="wallet-pay-btn">
                  <Wallet className="w-5 h-5" />
                  {isHindi ? `वॉलेट से भुगतान (बैलेंस: ₹${user?.wallet_balance})` : `Pay from Wallet (Balance: ₹${user?.wallet_balance})`}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
